import React, { useEffect, useState } from "react";
import { Card, Col, Row, Button, Form, Modal, Spinner, Table, Alert } from "react-bootstrap";
import { db, auth } from "../firebaseConfig";
import { collection, addDoc, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import ReactApexChart from "react-apexcharts";

export default function ManageLeaves() {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [rolePermissions, setRolePermissions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [fullName, setFullName] = useState("");

  const [totalRequests, setTotalRequests] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [approvedRequests, setApprovedRequests] = useState(0);

  useEffect(() => {
    const fetchUserRole = async () => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userDocRef = doc(db, "Users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userRole = userDoc.data().role || "";
            setCurrentUserRole(userRole);

            if (userRole === "admin") {
              setRolePermissions({ canManageLeaves: true });
            } else {
              const roleDocRef = doc(db, "Roles", userRole);
              const roleDoc = await getDoc(roleDocRef);

              if (roleDoc.exists()) {
                setRolePermissions(roleDoc.data().permissions);
              } else {
                setRolePermissions(null);
              }
            }
          }
        }
      });
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    if (rolePermissions && !rolePermissions.canManageLeaves && currentUserRole !== "admin") {
      setAccessDenied(true);
    }
  }, [rolePermissions]);

  useEffect(() => {
    const fetchUserAndLeaves = async () => {
      setLoading(true);
      try {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            const userDocRef = doc(db, "Users", user.uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              setFullName(userDoc.data().fullName || "Unknown User");
              await fetchLeaveRequests(user.email);
            }
          }
        });
      } catch (error) {
        console.error("Error fetching user or leaves:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndLeaves();
  }, []);

  const fetchLeaveRequests = async (email) => {
    setLoading(true);
    try {
      const leavesQuery = query(collection(db, "leave_requests"), where("email", "==", email));
      const querySnapshot = await getDocs(leavesQuery);
      const leaves = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setLeaveRequests(leaves);

      setTotalRequests(leaves.length);
      setPendingRequests(leaves.filter((leave) => leave.status === "Pending").length);
      setApprovedRequests(leaves.filter((leave) => leave.status === "Approved").length);
    } catch (error) {
      console.error("Error fetching leave requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLeaveRequest = async () => {
    if (!startDate || !endDate || !reason) {
      alert("Please fill in all fields.");
      return;
    }

    setLoading(true);
    try {
      const newLeaveRequest = {
        email: auth.currentUser.email,
        fullName: fullName,
        startDate: startDate,
        endDate: endDate,
        reason: reason,
        status: "Pending",
      };

      await addDoc(collection(db, "leave_requests"), newLeaveRequest);
      setLeaveRequests([...leaveRequests, newLeaveRequest]);
      setModalShow(false);
      fetchLeaveRequests(auth.currentUser.email);
    } catch (error) {
      console.error("Error adding leave request:", error);
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = {
    chart: {
      type: "pie",
      height: 300,
    },
    labels: ["Pending", "Approved", "Declined"],
    colors: ["#FFC107", "#28A745", "#DC3545"],
    legend: {
      position: "bottom",
    },
  };

  const chartSeries = [
    pendingRequests,
    approvedRequests,
    totalRequests - (pendingRequests + approvedRequests),
  ];

  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">You do not have permission to access this page.</p>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-leaf-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Manage Leaves</h1>
          </div>
          <p className="lead text-muted">Request and track your leave applications</p>
        </div>

        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="primary" size="lg" />
            <p className="text-secondary mt-3">Fetching your leave data...</p>
          </div>
        ) : (
          <>
            {/* Enhanced Statistics Cards */}
            <Row className="g-4 mb-5">
              <Col md="4">
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                      style={{ 
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                      }}>
                  <div className="bg-gradient-primary text-white p-4" 
                       style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-0 fw-bold">Total Requests</h6>
                        <h2 className="mb-0 fw-bold">{totalRequests}</h2>
                      </div>
                      <i className="ri-file-list-3-line fs-1 opacity-75"></i>
                    </div>
                  </div>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center">
                      <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                        <i className="ri-file-list-3-line text-primary fs-4"></i>
                      </div>
                      <div>
                        <p className="text-muted mb-0 small">All time requests</p>
                        <p className="fw-semibold mb-0">Leave Applications</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md="4">
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                      style={{ 
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                      }}>
                  <div className="bg-gradient-warning text-white p-4" 
                       style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-0 fw-bold">Pending Requests</h6>
                        <h2 className="mb-0 fw-bold">{pendingRequests}</h2>
                      </div>
                      <i className="ri-time-line fs-1 opacity-75"></i>
                    </div>
                  </div>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center">
                      <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                        <i className="ri-time-line text-warning fs-4"></i>
                      </div>
                      <div>
                        <p className="text-muted mb-0 small">Awaiting approval</p>
                        <p className="fw-semibold mb-0">Under Review</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md="4">
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                      style={{ 
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                      }}>
                  <div className="bg-gradient-success text-white p-4" 
                       style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h6 className="mb-0 fw-bold">Approved Requests</h6>
                        <h2 className="mb-0 fw-bold">{approvedRequests}</h2>
                      </div>
                      <i className="ri-check-line fs-1 opacity-75"></i>
                    </div>
                  </div>
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center">
                      <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                        <i className="ri-check-line text-success fs-4"></i>
                      </div>
                      <div>
                        <p className="text-muted mb-0 small">Successfully approved</p>
                        <p className="fw-semibold mb-0">Confirmed Leaves</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4">
              <Col md="6">
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                  <div className="bg-gradient-info text-white p-4" 
                       style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <div className="d-flex align-items-center">
                      <i className="ri-pie-chart-line fs-4 me-3"></i>
                      <h5 className="mb-0 fw-bold">Leave Requests Summary</h5>
                    </div>
                  </div>
                  <Card.Body className="p-4">
                    <ReactApexChart options={chartOptions} series={chartSeries} type="pie" height={300} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md="6">
                <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                  <div className="bg-gradient-success text-white p-4" 
                       style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="ri-file-list-3-line fs-4 me-3"></i>
                        <h5 className="mb-0 fw-bold">Leave History</h5>
                      </div>
                      <span className="badge bg-white text-success px-3 py-2 rounded-pill">
                        {leaveRequests.length} requests
                      </span>
                    </div>
                  </div>
                  <Card.Body className="p-0">
                    {leaveRequests.length > 0 ? (
                      <div className="table-responsive">
                        <Table className="table-hover table-borderless mb-0">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th className="border-0">
                                <i className="ri-calendar-event-line me-2"></i>Start Date
                              </th>
                              <th className="border-0">
                                <i className="ri-calendar-check-line me-2"></i>End Date
                              </th>
                              <th className="border-0">
                                <i className="ri-file-text-line me-2"></i>Reason
                              </th>
                              <th className="border-0">
                                <i className="ri-flag-line me-2"></i>Status
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaveRequests.map((leave, index) => (
                              <tr key={index}
                                  style={{ 
                                    cursor: "pointer",
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                    e.currentTarget.style.transform = 'scale(1.01)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}>
                                <td className="fw-semibold">{leave.startDate}</td>
                                <td className="fw-semibold">{leave.endDate}</td>
                                <td className="text-truncate" style={{ maxWidth: '150px' }} title={leave.reason}>
                                  {leave.reason}
                                </td>
                                <td>
                                  <span
                                    className={`badge rounded-pill px-3 py-2 fw-semibold ${
                                      leave.status === "Approved"
                                        ? "bg-success"
                                        : leave.status === "Declined"
                                        ? "bg-danger"
                                        : "bg-warning text-dark"
                                    }`}
                                  >
                                    {leave.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div className="mb-4">
                          <i className="ri-file-list-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h5 className="text-muted mb-3">No Leave Requests Found</h5>
                        <p className="text-muted">You haven't submitted any leave requests yet.</p>
                        <Button 
                          variant="primary" 
                          onClick={() => setModalShow(true)}
                          className="mt-3 px-4 py-2 rounded-pill fw-semibold"
                        >
                          <i className="ri-add-line me-2"></i>
                          Submit First Request
                        </Button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Request Leave Button */}
            <div className="text-center mt-5">
              <Button 
                variant="success" 
                size="lg"
                onClick={() => setModalShow(true)}
                className="px-5 py-3 rounded-pill fw-semibold shadow-lg"
                style={{ 
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <i className="ri-add-line me-2"></i>
                Request Leave
              </Button>
            </div>
          </>
        )}

        <Footer />

        {/* Enhanced Request Leave Modal */}
        <Modal show={modalShow} onHide={() => setModalShow(false)} centered size="lg">
          <Modal.Header className="bg-gradient-success text-white border-0">
            <Modal.Title className="fw-bold">
              <i className="ri-leaf-line me-2"></i>
              Request Leave
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">
                  <i className="ri-calendar-event-line me-2"></i>Start Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-control-lg shadow-sm border-2"
                  style={{ borderRadius: '12px' }}
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">
                  <i className="ri-calendar-check-line me-2"></i>End Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-control-lg shadow-sm border-2"
                  style={{ borderRadius: '12px' }}
                />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">
                  <i className="ri-file-text-line me-2"></i>Reason
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for leave"
                  className="shadow-sm border-2"
                  style={{ borderRadius: '12px' }}
                />
                <Form.Text className="text-muted">
                  <i className="ri-information-line me-1"></i>
                  Please provide a detailed reason for your leave request.
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0 p-4">
            <Button variant="secondary" onClick={() => setModalShow(false)} className="px-4 py-2 rounded-pill fw-semibold">
              <i className="ri-close-line me-2"></i>Cancel
            </Button>
            <Button variant="success" onClick={handleAddLeaveRequest} className="px-4 py-2 rounded-pill fw-semibold">
              <i className="ri-send-plane-line me-2"></i>Submit Request
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
}
